/**
 * Portfolio Screen - MOBILE ONLY
 * 
 * Browse and select portfolios.
 * Tap portfolio → navigates to Properties List.
 * 
 * Structure (aligned with Home):
 * ScreenContainer
 * └── ScrollView
 *     ├── Header (Title + Add button)
 *     ├── SearchInput
 *     └── ListCard
 *         └── PortfolioCard items (rows, not nested cards)
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, ActivityIndicator, RefreshControl } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, touch, icon, layout } from '../../shared/tokens/mobile';
import { PortfolioWithStats } from '../../hooks/usePortfolios';
import { logger } from '../../shared/utils/logger';
import { usePortfolios } from '../../hooks/usePortfolios';
import {
  ScreenContainer,
  SearchInput,
  ListCard,
  PortfolioCard,
} from '../../components/v2/mobile';

// =============================================================================
// SCREEN
// =============================================================================

export default function PortfolioScreen() {
  const router = useRouter();
  
  // Data fetching via hook (Rule 6: no API calls in UI components)
  const {
    filteredPortfolios,
    search,
    setSearch,
    loading,
    refreshing,
    refresh,
  } = usePortfolios();

  // Handle portfolio press - navigate to opportunities list
  const handlePortfolioPress = useCallback((portfolio: PortfolioWithStats) => {
    logger.debug('Portfolio selected for navigation', 'portfolio', { 
      id: portfolio.id, 
      name: portfolio.name,
      opportunityCount: portfolio.opportunityCount,
      idType: typeof portfolio.id,
    });
    router.push({
      pathname: '/v2/portfolio/[id]',
      params: { id: String(portfolio.id) },
    });
  }, [router]);

  // Handle add press - modal to be added
  const handleAddPress = useCallback(() => {
    logger.debug('Add portfolio requested', 'portfolio');
  }, []);

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={color.primary} />
        }
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>Portfolio</Text>
          <Pressable
            onPress={handleAddPress}
            style={styles.addButton}
            accessibilityLabel="Add portfolio"
            accessibilityRole="button"
          >
            <Ionicons name="add" size={icon.lg} color={color.primary} />
          </Pressable>
        </View>

        {/* SEARCH */}
        <View style={styles.section}>
          <SearchInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search portfolios..."
          />
        </View>

        {/* LIST */}
        <View style={styles.section}>
          {loading ? (
            <View style={styles.loadingState}>
              <ActivityIndicator size="large" color={color.primary} />
            </View>
          ) : filteredPortfolios.length === 0 ? (
            <ListCard>
              <EmptyState hasSearch={!!search} />
            </ListCard>
          ) : (
            <ListCard>
              {filteredPortfolios.map((portfolio, index) => (
                <PortfolioCard
                  key={portfolio.id}
                  name={portfolio.name}
                  opportunityCount={portfolio.opportunityCount}
                  totalValue={portfolio.totalValue}
                  onPress={() => handlePortfolioPress(portfolio)}
                  hideBorder={index === filteredPortfolios.length - 1}
                />
              ))}
            </ListCard>
          )}
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function EmptyState({ hasSearch }: { hasSearch: boolean }) {
  return (
    <View style={styles.emptyState}>
      <Ionicons name="folder-open-outline" size={icon.xxl} color={color.textMuted} />
      <Text style={styles.emptyTitle}>
        {hasSearch ? 'No portfolios found' : 'No portfolios yet'}
      </Text>
      <Text style={styles.emptySubtitle}>
        {hasSearch ? 'Try a different search term' : 'Create your first portfolio to get started'}
      </Text>
    </View>
  );
}

// =============================================================================
// STYLES - Aligned with Home screen
// =============================================================================

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: space.md,
    paddingTop: space.md,
    paddingBottom: layout.scrollBottomPadding,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: space.lg,
  },
  title: {
    fontSize: text.pageTitle.fontSize,
    fontWeight: text.pageTitle.fontWeight,
    lineHeight: text.pageTitle.lineHeight,
    letterSpacing: text.pageTitle.letterSpacing,
    color: color.textPrimary,
  },
  addButton: {
    width: touch.minimum,
    height: touch.minimum,
    borderRadius: radius.full,
    backgroundColor: color.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  section: {
    marginBottom: space.lg,
  },
  loadingState: {
    paddingVertical: space.xxl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    paddingVertical: space.xxl,
    paddingHorizontal: space.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    fontSize: text.cardTitle.fontSize,
    fontWeight: text.cardTitle.fontWeight,
    color: color.textPrimary,
    marginTop: space.md,
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: text.body.fontSize,
    fontWeight: text.body.fontWeight,
    color: color.textSecondary,
    marginTop: space.xs,
    textAlign: 'center',
  },
});
